import { Link, Stack } from '@chakra-ui/react';
import { Status } from '@main/complykit/components/status';
import {
  useDeleteOrganizationTenantMutation,
  useInsertOrganizationTenantMutation,
} from '@main/graphql/features/Tenants.admin.generated';
import { useUpdateOrganizationMutation } from '@main/graphql/mutations/UpdateOrganization.admin.generated';
import { useGetOrganizationQuery } from '@main/graphql/queries/GetOrganization.generated';
import {
  DrawerProperty,
  DrawerPropertyMarkdown,
  EditableTag,
  errorToast,
  useDrawer,
} from '@main/ui';
import { BarsIcon, DoubleTagsIcon, TagIcon } from '@main/ui/icons';
import { ActionMeta, MultiValue } from 'chakra-react-select';

import { AgentTraceSwitch } from './agent-trace-switch';
import { ApiModuleSwitch } from './api-module-switch';
import { AuditTrailSwitch } from './audit-trail-switch';
import { ClientQuestionnaireSwitch } from './client-questionnaire-switch';
import { getRoleTagConfig } from './constants';
import { ControlAIReviewSwitch } from './control-ai-review-switch';
import { ControlsModuleSwitch } from './controls-module-switch';
import { DayZeroAIFeaturesSwitch } from './day-zero-ai-features-switch';
import { useGetOrgCustomRolesQuery } from './get-user-roles.admin.generated';
import { GroveSwitch } from './grove-switch';
import { InactivityTimeoutInput } from './inactivity-timeout-input';
import { PoliciesModuleSwitch } from './policies-module-switch';
import { PolicyAcknowledgementsSwitch } from './policy-acknowledgements-switch';
import { ProgramCreationSwitch } from './program-creation-switch';
import { RiskApprovalWorkflowsSwitch } from './risk-approval-workflows-switch';
import { RisksModuleSwitch } from './risks-module-switch';
import { TrustPortalSwitch } from './trust-portal-switch';
import { useUpdateOrganizationHandler } from './use-update-org-handler';
import { VendorApprovalWorkflowsSwitch } from './vendor-approval-workflows-switch';
import { VendorQuestionnaireAIReviewSwitch } from './vendor-questionnaire-ai-review-switch';
import { VendorsModuleSwitch } from './vendors-module-switch';

export const DetailsTab = ({ orgId }: { orgId: string }) => {
  const drawer = useDrawer();

  const { data, refetch: refetchOrg } = useGetOrganizationQuery({ orgId });
  const organization = data?.organizations_by_pk;

  const { data: rolesData } = useGetOrgCustomRolesQuery({
    orgId,
  });
  const [insertTenant] = useInsertOrganizationTenantMutation();
  const [deleteTenant] = useDeleteOrganizationTenantMutation();
  const [updateOrganization] = useUpdateOrganizationMutation();
  const updateOrgHanlder = useUpdateOrganizationHandler(orgId);

  const orgDefaultRole = rolesData?.roles?.find(
    (role) => role.id === organization?.default_user_role_id,
  );
  const orgTenants =
    organization?.tenants.map((tenant) => ({
      value: tenant.name,
      label: tenant.name,
      colorScheme: 'purple',
    })) || [];

  if (!organization) {
    return null;
  }

  const handleChange = async (
    items: MultiValue<(typeof orgTenants)[number]>,
    actionMeta: ActionMeta<(typeof orgTenants)[number]>,
  ) => {
    switch (actionMeta.action) {
      case 'create-option': {
        try {
          await insertTenant({
            orgTenant: {
              organization_id: organization.id,
              name: actionMeta.option.value,
            },
          }).unwrap();
        } catch {
          errorToast('Unable to create tenant');
        }
        break;
      }

      case 'remove-value':
      case 'pop-value': {
        try {
          await deleteTenant({
            name: actionMeta.removedValue.value,
          }).unwrap();
        } catch {
          errorToast('Unable to delete tenant');
        }
        break;
      }
    }
    refetchOrg();
  };

  return (
    <Stack spacing={2.5}>
      {organization.parent_organization_id && (
        <DrawerProperty>
          <DrawerProperty.Label icon={BarsIcon}>Parent org id</DrawerProperty.Label>
          <DrawerProperty.Content px={2}>
            <Link
              onClick={() => {
                drawer.open({
                  entity: 'organization',
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  entityId: organization.parent_organization_id!,
                });
              }}
            >
              {organization.parent_organization_id}
            </Link>
          </DrawerProperty.Content>
        </DrawerProperty>
      )}

      <DrawerProperty>
        <DrawerProperty.Label icon={BarsIcon}>Description</DrawerProperty.Label>
        <DrawerProperty.Content>
          <DrawerPropertyMarkdown
            defaultValue={organization.description}
            placeholder="Click to add description"
            onSubmit={(value) => {
              updateOrgHanlder(
                updateOrganization({
                  organization_id: orgId,
                  updatePayload: {
                    description: value,
                  },
                }),
              );
            }}
          />
        </DrawerProperty.Content>
      </DrawerProperty>

      <DrawerProperty>
        <DrawerProperty.Label icon={TagIcon}>Status</DrawerProperty.Label>
        <DrawerProperty.Content px={2} display="flex" alignItems="center">
          {organization.disabled ? (
            <Status tone="neutral">Disable</Status>
          ) : (
            <Status tone="success">Active</Status>
          )}
        </DrawerProperty.Content>
      </DrawerProperty>

      <DrawerProperty>
        <DrawerProperty.Label icon={DoubleTagsIcon}>Tenant</DrawerProperty.Label>
        <DrawerProperty.Content>
          <EditableTag
            isMulti={true}
            value={orgTenants}
            getNewOptionData={(label) => ({ label, value: label, colorScheme: 'purple' })}
            onChange={handleChange}
            variant="inline"
          />
        </DrawerProperty.Content>
      </DrawerProperty>

      <DrawerProperty>
        <DrawerProperty.Label
          icon={TagIcon}
          tooltip='Default role assigned during user joining via SSO. If left empty, "Member" role will be used'
        >
          User role
        </DrawerProperty.Label>
        <DrawerProperty.Content>
          <EditableTag
            isClearable={true}
            value={orgDefaultRole && getRoleTagConfig(orgDefaultRole)}
            options={rolesData?.roles.map((role) => getRoleTagConfig(role))}
            onChange={(option) => {
              updateOrgHanlder(
                updateOrganization({
                  organization_id: orgId,
                  updatePayload: {
                    default_user_role_id: option?.value ?? null,
                  },
                }),
              );
            }}
          />
        </DrawerProperty.Content>
      </DrawerProperty>

      <InactivityTimeoutInput orgId={orgId} />
      <ControlsModuleSwitch orgId={orgId} />
      <ProgramCreationSwitch orgId={orgId} />
      <RisksModuleSwitch orgId={orgId} />
      <RiskApprovalWorkflowsSwitch orgId={orgId} />
      <VendorsModuleSwitch orgId={orgId} />
      <VendorApprovalWorkflowsSwitch orgId={orgId} />
      <PoliciesModuleSwitch orgId={orgId} />
      <PolicyAcknowledgementsSwitch orgId={orgId} />
      <ControlAIReviewSwitch orgId={orgId} />
      <VendorQuestionnaireAIReviewSwitch orgId={orgId} />
      <DayZeroAIFeaturesSwitch orgId={orgId} />
      <GroveSwitch orgId={orgId} />
      <AgentTraceSwitch orgId={orgId} />
      <ClientQuestionnaireSwitch orgId={orgId} />
      <TrustPortalSwitch orgId={orgId} />
      <ApiModuleSwitch orgId={orgId} />
      <AuditTrailSwitch orgId={orgId} />
    </Stack>
  );
};
